import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  ChevronDownIcon,
  ClockIcon,
  ListIcon,
  MoreHorizontalIcon,
  PlusIcon,
  ZapIcon,
} from 'lucide-react';
import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible.js';
import { useHintTarget } from '@/features/onboarding/useHintTarget.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import type { Automation } from '@/integration/types.js';
import {
  automationSummariesQueryOptions,
  automationsQueryOptions,
  extractScheduleText,
  isFireBlockedAutomation,
  useDeleteAutomation,
  useFireNowAutomation,
} from '@/lib/api/automations.js';
import { isAutomationsInAgentEnabled } from '@/lib/api/feature-flags.js';
import { notify } from '@/lib/notify.js';
import { cn } from '@/lib/utils.js';

import {
  sidebarIconClass,
  sidebarRowActionClass,
  sidebarRowActive,
  sidebarRowBase,
  sidebarRowHover,
} from '../../app/sidebar-primitives.js';

import { AutomationShareDialogById } from './AutomationShareDialogById.js';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog.js';
import { CreateAutomationDialog } from './CreateAutomationDialog.js';
import { cronHumanize } from './cron-humanize.js';
import { SharedAutomationRow } from './SharedAutomationRow.js';

interface SidebarAutomationsProps {
  collapsed?: boolean;
  onNavigate?: () => void;
}

/**
 * Sidebar "Scheduled" section. The title navigates to the full /scheduled UI; a sibling chevron
 * expands an inline list of the user's automation chats (1 automation = 1 dedicated chat). Each row's
 * actions menu edits the automation on its full detail page.
 */
export function SidebarAutomations({
  collapsed = false,
  onNavigate,
}: SidebarAutomationsProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  // Scope the inline list to the selected workspace so it mirrors the /scheduled screen — showing
  // only this workspace's automations, and never opening a chat that would yank the user into another
  // workspace. (The cross-workspace dedicated-chat SUBTRACTION lives in a separate hook that keeps the
  // unscoped list.)
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);
  const { data, isLoading } = useQuery(
    automationsQueryOptions(selectedWorkspaceId),
  );
  const automations = data ?? [];
  /**
   * Automations shared with the caller, not owned by them: the same v2 list `/scheduled` reads,
   * filtered to the rows the caller cannot manage. Rendered after the own rows, server order kept.
   */
  const { data: summaries } = useQuery(
    automationSummariesQueryOptions(selectedWorkspaceId),
  );
  const sharedAutomations = (summaries ?? []).filter(
    (summary) => !summary.canManage,
  );
  /** One id, two mount points — only the branch matching the rail state is ever in the tree. */
  const automationsHintRef = useHintTarget<HTMLElement>(
    'nav.automations',
    'gate',
  );

  // Collapsed rail: icon-only link straight to the full UI (no inline expansion).
  if (collapsed) {
    return (
      <div className="min-w-0 px-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              ref={automationsHintRef}
              to="/scheduled"
              onClick={onNavigate}
              className="group sidebar-nav-link w-full"
            >
              <ClockIcon className={sidebarIconClass} />
              <span className="sidebar-label sidebar-fade truncate text-left">
                Scheduled
              </span>
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">Scheduled</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <Collapsible
        open={open}
        onOpenChange={setOpen}
        className="sidebar-fade min-w-0 px-3"
      >
        {/* The label + chevron form ONE toggle trigger (mirrors the Projects header's single-trigger
            "Projects ⌄"): clicking the word "Scheduled" only expands/collapses the inline list, it no
            longer navigates. Navigation to the full /scheduled page now lives in a dedicated trailing
            List icon — hover-revealed on desktop, always visible on touch (no hover) — so a click on the
            name never yanks the user off to another page. */}
        <div
          data-hint-beacon-row
          className={cn(sidebarRowBase, sidebarRowHover, 'group/authead')}
        >
          <CollapsibleTrigger asChild>
            <button
              ref={automationsHintRef}
              type="button"
              className="group flex min-w-0 flex-1 items-center gap-1.5 max-md:gap-3 cursor-pointer text-inherit outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-foreground/20 rounded-[6px]"
            >
              <ClockIcon className={sidebarIconClass} />
              <span className="sidebar-label sidebar-fade truncate text-left">
                Scheduled
              </span>
              <ChevronDownIcon
                data-chevron
                className={cn(
                  sidebarIconClass,
                  'transition-transform duration-200 ease-out',
                  open ? 'rotate-0' : '-rotate-90',
                )}
              />
            </button>
          </CollapsibleTrigger>
          {/* New automation — opens the create dialog from anywhere. Stays visible while its dialog
              is open and on keyboard focus (the action class is opacity-0-until-hover on desktop). */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="New in Scheduled"
                onClick={(e) => {
                  e.stopPropagation();
                  setCreateOpen(true);
                }}
                className={cn(
                  sidebarRowActionClass,
                  'md:group-hover/authead:opacity-100 focus-visible:opacity-100',
                  createOpen && 'opacity-100',
                )}
              >
                <PlusIcon className="size-4" strokeWidth={1.75} aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">New in Scheduled</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                asChild
                variant="ghost"
                size="icon-sm"
                aria-label="All in Scheduled"
                className={cn(
                  sidebarRowActionClass,
                  'md:group-hover/authead:opacity-100 focus-visible:opacity-100',
                )}
              >
                <Link to="/scheduled" onClick={onNavigate}>
                  <ListIcon className="size-4" strokeWidth={1.75} aria-hidden />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">All in Scheduled</TooltipContent>
          </Tooltip>
        </div>

        {/* pl-3 indent so the sub-rows read as nested under the section, not flush with top-level nav. */}
        <CollapsibleContent className="flex flex-col gap-0.5 pt-0.5 pl-3">
          {isLoading && (
            <div className="flex flex-col gap-[6px] px-[10px] py-1">
              <Skeleton className="h-8 w-full rounded-[6px]" />
              <Skeleton className="h-8 w-3/4 rounded-[6px]" />
            </div>
          )}
          {!isLoading &&
            automations.length === 0 &&
            sharedAutomations.length === 0 && (
              <p className="sidebar-fade px-[10px] pb-2 text-xs text-sidebar-section/70">
                Nothing scheduled yet
              </p>
            )}
          {automations.map((a) => (
            <AutomationChatRow
              key={a.id}
              automation={a}
              onNavigate={onNavigate}
            />
          ))}
          {sharedAutomations.map((summary) => (
            <SharedAutomationRow
              key={summary.id}
              automation={summary}
              onNavigate={onNavigate}
            />
          ))}
        </CollapsibleContent>
      </Collapsible>

      {/* Create dialog driven by the header "+" — expand the list and open the new automation's page. */}
      <CreateAutomationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => {
          setOpen(true);
          void navigate({ to: '/scheduled/$id', params: { id } });
          onNavigate?.();
        }}
      />
    </div>
  );
}

function AutomationChatRow({
  automation,
  onNavigate,
}: {
  automation: Automation;
  onNavigate?: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const router = useRouterState();
  const fireNow = useFireNowAutomation();
  const remove = useDeleteAutomation();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Fire-now is gated behind the same admin flag as the /scheduled list (fail-open → visible by
  // default), and only on rows backed by a dedicated chat — otherwise a fired run has nowhere to land.
  const showFireNow = isAutomationsInAgentEnabled();
  const conversationId = automation.target?.conversationId;
  const isDedicated =
    automation.target?.dedicated === true &&
    typeof conversationId === 'string' &&
    conversationId.length > 0;
  const activeConvId = readSearchParam(
    router.location.search,
    'conversationId',
  );
  const active =
    conversationId !== undefined && activeConvId === conversationId;
  const name = automation.name?.trim() || 'Untitled';

  function open(): void {
    // Dedicated automations open their chat directly; ones without a chat fall back to the detail page.
    if (typeof conversationId === 'string' && conversationId.length > 0) {
      void navigate({ to: '/chats', search: { conversationId } as never });
    } else {
      void navigate({ to: '/scheduled/$id', params: { id: automation.id } });
    }
    onNavigate?.();
  }

  /**
   * Edit always opens the automation's full detail page, whatever its capability. That page reads and
   * writes every body shape (`payload.task.prompt`, the legacy `payload.prompt`, a check-in's
   * `payload.question`) and carries every field, so no row edits in a lighter modal that would show a
   * blank body or save to the wrong key.
   */
  function edit(): void {
    void navigate({ to: '/scheduled/$id', params: { id: automation.id } });
    onNavigate?.();
  }

  // Deleting cascades a soft-delete of the dedicated chat (BFF). If that chat is the one currently
  // open, leave it so the user isn't stranded on a deleted conversation.
  function confirmDelete(): void {
    setConfirmDeleteOpen(false);
    remove.mutate(automation.id, {
      onSuccess: () => {
        notify.success(`“${name}” deleted`);
        if (active) void navigate({ to: '/home' });
      },
    });
  }

  return (
    <div
      className={cn(
        sidebarRowBase,
        active ? sidebarRowActive : sidebarRowHover,
        'group/autorow',
        !automation.enabled && 'opacity-60',
      )}
    >
      <button
        type="button"
        onClick={open}
        title={`${name} • ${cronHumanize(automation.schedule, extractScheduleText(automation.payload))}${automation.enabled ? '' : ' • paused'}`}
        className="flex min-w-0 flex-1 items-center gap-1.5 max-md:gap-3 text-left text-inherit"
      >
        <ZapIcon className={sidebarIconClass} strokeWidth={1.75} />
        <span className="flex-1 truncate text-inherit">{name}</span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Actions for ${name}`}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              sidebarRowActionClass,
              'md:group-hover/autorow:opacity-100 focus-visible:opacity-100',
              // Keep the trigger visible while its (portaled) menu is open, even if the mouse leaves.
              'data-[state=open]:opacity-100',
            )}
          >
            <MoreHorizontalIcon
              className="size-4"
              strokeWidth={1.75}
              aria-hidden
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start">
          <DropdownMenuItem onSelect={() => edit()}>Edit</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setShareOpen(true)}>
            Share
          </DropdownMenuItem>
          {showFireNow && isDedicated && (
            <DropdownMenuItem
              disabled={
                fireNow.isPending || isFireBlockedAutomation(automation)
              }
              onSelect={() =>
                fireNow.mutate(automation.id, {
                  onSuccess: () => notify.success(`“${name}” fired`),
                })
              }
            >
              Fire now
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            variant="destructive"
            onSelect={() => setConfirmDeleteOpen(true)}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        onConfirm={confirmDelete}
        name={name}
      />

      <AutomationShareDialogById
        automationId={automation.id}
        conversationId={isDedicated ? conversationId : undefined}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
    </div>
  );
}

function readSearchParam(search: unknown, key: string): string | null {
  if (typeof search === 'string') {
    const sp = new URLSearchParams(
      search.startsWith('?') ? search.slice(1) : search,
    );
    return sp.get(key);
  }
  if (search !== null && typeof search === 'object' && key in search) {
    const v = (search as Record<string, unknown>)[key];
    return typeof v === 'string' ? v : null;
  }
  return null;
}
